/**
 * Parity with legacy `UtilService.userRole === 'ROLE_ADMIN'` (app user `roles[0].role.type`)
 * and Keycloak JWT (`realm_access` + `resource_access` client roles).
 */

/** `ROLE_ADMIN` anywhere on Keycloak token (realm or client roles). */
export function keycloakTokenHasRole(tokenParsed: Record<string, unknown> | null | undefined, role: string): boolean {
  if (!tokenParsed || typeof tokenParsed !== 'object') return false;
  const ra = tokenParsed['realm_access'] as { roles?: string[] } | undefined;
  if (Array.isArray(ra?.roles) && ra.roles.includes(role)) return true;
  const res = tokenParsed['resource_access'] as Record<string, { roles?: string[] }> | undefined;
  if (res && typeof res === 'object') {
    for (const v of Object.values(res)) {
      if (v && Array.isArray(v.roles) && v.roles.includes(role)) return true;
    }
  }
  return false;
}

/**
 * Best-effort read of primary app role type from `user-env` / session payload
 * (legacy: `UserDto.roles[0].role.type`). Supports extra fields the API may add later.
 */
export function userEnvPrimaryRoleType(env: Record<string, unknown> | null | undefined): string | undefined {
  if (!env || typeof env !== 'object') return undefined;
  const direct = env['primary_role_type'];
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  const user = env['user'] as Record<string, unknown> | undefined;
  const roles = (user?.['roles'] ?? env['roles']) as unknown[] | undefined;
  const first = roles?.[0] as Record<string, unknown> | undefined;
  const roleObj = first?.['role'] as Record<string, unknown> | undefined;
  const type = roleObj?.['type'];
  return typeof type === 'string' && type.trim() ? type.trim() : undefined;
}

/**
 * Show admin-only UI (e.g. preference user/role targeting):
 * legacy `user-env` / `user/my` app role (`roles[0].role.type`) or JWT `ROLE_ADMIN`.
 */
export function isAppOrTokenRoleAdmin(
  tokenParsed: Record<string, unknown> | null | undefined,
  userEnv: Record<string, unknown> | null | undefined,
  /** Same shape as GET `user/my` (legacy layout loads this for `userRole`). */
  userMyProfile?: Record<string, unknown> | null | undefined
): boolean {
  if (userEnvPrimaryRoleType(userEnv) === 'ROLE_ADMIN') return true;
  if (keycloakTokenHasRole(tokenParsed, 'ROLE_ADMIN')) return true;
  if (userMyProfile && userEnvPrimaryRoleType({ user: userMyProfile }) === 'ROLE_ADMIN') return true;
  return false;
}
